import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, ChevronLeft, X, SkipForward } from 'lucide-react';
import { trouverCible, attendreCible, resoudreRoute } from '../tours/moteur';

/**
 * GuidedTour — visite guidée pas à pas, par-dessus le portail (moteur étendu le 05/10/2026,
 * spec visite-guidee/2026-10-05-premiers-pas). Logique pure dans tours/moteur.js.
 *
 * Usage :
 *   <GuidedTour steps={etapes} tourId="premiers_pas" startIndex={0}
 *     onEtape={(step, index) => {}} onComplete={(raison, step) => {}} preparer={preparer} />
 *
 * Format d'étape :
 *   { id: 'tickets-vues',                  // identifiant stable : reprise (startIndex) et onEtape
 *     route: '/tickets',                   // ou (ctx) => '/tickets/…' | null ; absente = page courante.
 *                                          //   Fonction qui rend null = donnée absente → messageSansDonnee
 *     prepare: 'ticket',                   // clé passée UNE fois par visite à preparer(cle, ctx) -> Promise<object>,
 *                                          //   fusionné dans le contexte lu par route(ctx)
 *     target: '[data-tour="…"]',           // ou tableau de replis ; null = bulle centrée
 *     title: '…', content: '…',            // retours à la ligne gardés (white-space: pre-line)
 *     position: 'bottom',                  // top | bottom | left | right (ajustée si ça déborde)
 *     action: '…',                         // indication facultative sous le texte
 *     permission, permissions, roles,      // droits : filtrés EN AMONT (moteur.filtrerEtapes)
 *     demo: 'appel',                       // lu par l'appelant dans onEtape (démonstration à afficher)
 *     messageSansDonnee: '…' }             // remplace content quand la donnée de l'étape manque
 *
 * Déroulé d'une étape : onEtape(step, index) dès son DÉBUT (avant l'attente de la cible, pour que
 * l'appelant affiche ce qu'elle éclaire) → préparation → changement de page si besoin → cible attendue
 * jusqu'à 5 s ; absente, la bulle se centre et le dit.
 * Fin : onComplete('termine' | 'passe', step). La mémorisation appartient à l'appelant (préférences
 * utilisateur) : plus de localStorage ici. Un clic sur le fond ne ferme plus la visite (« Passer le guide »
 * ou la croix seulement).
 */

const PADDING = 8;
const TOOLTIP_GAP = 14;
const DELAI_PLACEMENT_MS = 350;
const CENTRE = { top: '50%', left: '50%', transform: 'translate(-50%,-50%)' };

// ── Rect computation ─────────────────────────────────────────────────────────

function rectDe(el) {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { top: r.top - PADDING, left: r.left - PADDING, width: r.width + PADDING * 2, height: r.height + PADDING * 2, el };
}

function computeTooltipPosition(rect, preferred, tooltipRef) {
  if (!rect || !tooltipRef.current) return CENTRE;
  const tt = tooltipRef.current.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const positions = {
    bottom: { top: rect.top + rect.height + TOOLTIP_GAP, left: rect.left + rect.width / 2 - tt.width / 2 },
    top:    { top: rect.top - tt.height - TOOLTIP_GAP, left: rect.left + rect.width / 2 - tt.width / 2 },
    right:  { top: rect.top + rect.height / 2 - tt.height / 2, left: rect.left + rect.width + TOOLTIP_GAP },
    left:   { top: rect.top + rect.height / 2 - tt.height / 2, left: rect.left - tt.width - TOOLTIP_GAP },
  };

  const fits = (pos) => pos.top >= 8 && pos.left >= 8 && pos.top + tt.height <= vh - 8 && pos.left + tt.width <= vw - 8;

  const order = [preferred, 'bottom', 'right', 'top', 'left'];
  for (const dir of order) {
    if (fits(positions[dir])) return { top: positions[dir].top, left: positions[dir].left };
  }
  // Fallback: clamp
  const p = positions[preferred];
  return { top: Math.max(8, Math.min(vh - tt.height - 8, p.top)), left: Math.max(8, Math.min(vw - tt.width - 8, p.left)) };
}

// ── Component ────────────────────────────────────────────────────────────────

export default function GuidedTour({ steps, tourId, onComplete, active = true, startIndex = 0, onEtape, preparer }) {
  const [current, setCurrent] = useState(startIndex);
  const [rect, setRect] = useState(null);
  const [tooltipPos, setTooltipPos] = useState(CENTRE);
  const [visible, setVisible] = useState(false);
  const [manque, setManque] = useState(null); // null | 'zone' (cible absente) | 'donnee' (rien à montrer)
  const tooltipRef = useRef(null);
  const ctxRef = useRef({});            // contexte des routes (ticketId, posteId…) rempli par preparer
  const preparationsRef = useRef({});   // une promesse par clé de préparation, partagée par ses étapes
  const placementRef = useRef(null);    // placement différé en attente (annulé à chaque changement)
  const annulerRef = useRef(null);      // arrête le déroulé de l'étape en cours (fin ou abandon)
  const navigate = useNavigate();

  const step = steps[current];

  const centrer = useCallback(() => {
    clearTimeout(placementRef.current);
    setRect(null);
    setTooltipPos(CENTRE);
  }, []);

  // Fait défiler jusqu'à la cible, puis l'éclaire une fois le défilement fini.
  const updatePosition = useCallback(() => {
    if (!step) return;
    clearTimeout(placementRef.current);
    const el = trouverCible(step.target);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      placementRef.current = setTimeout(() => {
        const fresh = rectDe(trouverCible(step.target));
        setRect(fresh);
        if (!fresh) setTooltipPos(CENTRE);
        else if (tooltipRef.current) setTooltipPos(computeTooltipPosition(fresh, step.position || 'bottom', tooltipRef));
      }, DELAI_PLACEMENT_MS);
    } else {
      setRect(null);
      setTooltipPos(CENTRE);
    }
  }, [step]);

  useEffect(() => {
    if (!active || !step) return undefined;
    let annule = false;
    annulerRef.current = () => { annule = true; };
    setManque(null); // le message de l'étape précédente ne déborde pas sur celle-ci
    // Cible pas (encore) à l'écran : on éteint tout de suite le projecteur de l'étape précédente
    // (si elle est déjà là, la bulle glisse d'une cible à l'autre sans clignoter).
    if (!trouverCible(step.target)) centrer();
    onEtape?.(step, current);
    const sansDonnee = () => {
      setManque('donnee');
      centrer();
      setVisible(true);
    };
    (async () => {
      try {
        if (step.prepare && preparer) {
          const cle = step.prepare;
          if (!preparationsRef.current[cle]) {
            preparationsRef.current[cle] = Promise.resolve()
              .then(() => preparer(cle, ctxRef.current))
              .then((res) => { ctxRef.current = { ...ctxRef.current, ...(res || {}) }; })
              .catch((err) => { console.warn('[visite] préparation « %s » en échec', cle, err); });
          }
          await preparationsRef.current[cle];
          if (annule) return; // étape quittée ou visite fermée pendant la préparation : on ne navigue plus
        }
        const route = resoudreRoute(step, ctxRef.current);
        if (step.route && !route) {
          sansDonnee();
          return;
        }
        if (route && window.location.pathname !== route.split('#')[0]) {
          centrer(); // l'ancienne cible quitte l'écran avec sa page
          navigate(route);
        }
        const el = await attendreCible(step.target);
        if (annule) return;
        setManque(step.target && !el ? 'zone' : null);
        setVisible(true);
        updatePosition();
      } catch (err) {
        // Ex. une route(ctx) qui lève : la bulle s'affiche quand même, avec le message « sans donnée ».
        console.warn('[visite] étape « %s » en échec', step.id, err);
        if (!annule) sansDonnee();
      }
    })();
    const handleResize = () => updatePosition();
    window.addEventListener('resize', handleResize);
    return () => {
      annule = true;
      clearTimeout(placementRef.current);
      window.removeEventListener('resize', handleResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, active]);

  // Second pass for tooltip positioning after render
  useEffect(() => {
    if (!visible || !rect || !tooltipRef.current) return undefined;
    const timer = setTimeout(() => {
      setTooltipPos(computeTooltipPosition(rect, step?.position || 'bottom', tooltipRef));
    }, 50);
    return () => clearTimeout(timer);
  }, [visible, rect, step]);

  const next = () => {
    if (current < steps.length - 1) {
      setCurrent(c => c + 1);
    } else {
      finish();
    }
  };

  const prev = () => {
    if (current > 0) setCurrent(c => c - 1);
  };

  // Fermeture : même si l'appelant garde le composant monté, rien de l'étape en cours ne reprend après.
  const arreter = () => {
    annulerRef.current?.();
    clearTimeout(placementRef.current);
    setVisible(false);
  };

  const finish = () => {
    arreter();
    onComplete?.('termine', step);
  };

  const skip = () => {
    arreter();
    onComplete?.('passe', step);
  };

  if (!visible || !step || !active) return null;

  const progress = ((current + 1) / steps.length) * 100;

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 100000 }}>
      {/* Fond avec la découpe du projecteur — non cliquable : un clic à côté ne fait plus perdre la visite */}
      <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
        <defs>
          <mask id="tour-spotlight">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {rect && (
              <rect
                x={rect.left} y={rect.top} width={rect.width} height={rect.height}
                rx="8" ry="8" fill="black"
              />
            )}
          </mask>
        </defs>
        <rect x="0" y="0" width="100%" height="100%" fill="rgba(0,0,0,0.55)" mask="url(#tour-spotlight)" />
      </svg>

      {/* Spotlight border highlight */}
      {rect && (
        <div style={{
          position: 'absolute', top: rect.top, left: rect.left,
          width: rect.width, height: rect.height,
          border: '2px solid rgba(91,163,230,0.7)', borderRadius: 8,
          boxShadow: '0 0 0 4px rgba(91,163,230,0.15), 0 0 20px rgba(91,163,230,0.2)',
          pointerEvents: 'none', transition: 'all 0.35s cubic-bezier(0.4,0,0.2,1)',
        }} />
      )}

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        data-testid="visite-bulle"
        onClick={e => e.stopPropagation()}
        style={{
          position: 'absolute',
          top: tooltipPos.top,
          left: tooltipPos.left,
          transform: tooltipPos.transform || 'none',
          width: 360, maxWidth: 'calc(100vw - 32px)',
          background: '#fff', borderRadius: 12,
          boxShadow: '0 8px 32px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.08)',
          fontFamily: "'IBM Plex Sans', sans-serif",
          overflow: 'hidden',
          transition: 'top 0.35s cubic-bezier(0.4,0,0.2,1), left 0.35s cubic-bezier(0.4,0,0.2,1)',
        }}
      >
        {/* Progress bar */}
        <div style={{ height: 3, background: '#e5e7eb' }}>
          <div style={{
            height: '100%', width: `${progress}%`,
            background: 'linear-gradient(90deg, #0099D6, #5ba3e6)',
            transition: 'width 0.3s ease',
          }} />
        </div>

        {/* Header */}
        <div style={{
          padding: '14px 16px 0',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#0099D6', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>
              Étape {current + 1} / {steps.length}
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#111827', lineHeight: 1.3 }}>
              {step.title}
            </div>
          </div>
          <button onClick={skip} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: 4,
            color: '#9ca3af', display: 'flex', borderRadius: 4,
          }} title="Fermer le guide">
            <X size={16} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '10px 16px 16px', fontSize: 13, color: '#374151', lineHeight: 1.6, whiteSpace: 'pre-line' }}>
          {manque === 'donnee' ? (step.messageSansDonnee || 'Rien à te montrer ici pour le moment.') : step.content}
          {manque === 'zone' && (
            <div style={{ marginTop: 8, fontSize: 12, color: '#92400e' }}>Cette zone n'est pas affichée pour le moment.</div>
          )}
        </div>

        {/* Action hint */}
        {step.action && (
          <div style={{
            margin: '0 16px 12px', padding: '8px 12px',
            background: '#f0f9ff', borderRadius: 6, border: '1px solid #bae6fd',
            fontSize: 12, color: '#0369a1', fontWeight: 500,
          }}>
            {step.action}
          </div>
        )}

        {/* Navigation */}
        <div style={{
          padding: '10px 16px', borderTop: '1px solid #f3f4f6',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: '#fafafa',
        }}>
          <button onClick={skip} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 12, color: '#9ca3af', display: 'flex', alignItems: 'center', gap: 4,
            fontFamily: "'IBM Plex Sans', sans-serif",
          }}>
            <SkipForward size={12} /> Passer le guide
          </button>

          <div style={{ display: 'flex', gap: 6 }}>
            {current > 0 && (
              <button onClick={prev} style={navBtnStyle}>
                <ChevronLeft size={14} /> Précédent
              </button>
            )}
            <button onClick={next} style={{ ...navBtnStyle, background: '#0099D6', color: '#fff', border: 'none' }}>
              {current === steps.length - 1 ? 'Terminer' : 'Suivant'} <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

const navBtnStyle = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '6px 14px', fontSize: 12, fontWeight: 600,
  borderRadius: 6, cursor: 'pointer',
  border: '1px solid #d1d5db', background: '#fff', color: '#374151',
  fontFamily: "'IBM Plex Sans', sans-serif",
  transition: 'background 0.15s',
};
